import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import "@/App.css";
import { PageHeader, InstrumentCard } from "@/sections";

const FILTERS = [
  { value: "all", label: "All instruments" },
  { value: "flute", label: "Pharaoh Love Flutes" },
  { value: "drum", label: "Drums" },
  { value: "didgeridoo", label: "Didgeridoos" },
];

export default function GalleryPage() {
  const [content, setContent] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/instruments")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const counts = {
    all: items.length,
    flute: items.filter((i) => i.type === "flute").length,
    drum: items.filter((i) => i.type === "drum").length,
    didgeridoo: items.filter((i) => i.type === "didgeridoo").length,
  };

  const featured = items.filter((i) => i.featured);
  const visible = filter === "all" ? items : items.filter((i) => i.type === filter);

  return (
    <div className="App">
      <Nav artisanName={content?.artisan_name || "Sacred Instruments"} />
      <PageHeader
        eyebrow="The Gallery"
        title="Every piece from the studio."
        subtitle="A complete view of all hand-crafted Pharaoh Love Flutes, drums and didgeridoos currently in the studio. Click any piece to hear its voice."
      />

      {/* Filter bar */}
      <section className="px-6 md:px-12 lg:px-24 pb-10">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 md:gap-3" data-testid="gallery-filters">
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-5 py-2.5 rounded-full text-sm tracking-wide transition-colors border ${
                  active
                    ? "bg-[var(--si-terracotta)] text-white border-[var(--si-terracotta)]"
                    : "bg-transparent text-[var(--si-text)] border-[var(--si-border)] hover:border-[var(--si-text)]"
                }`}
                data-testid={`filter-${f.value}`}
              >
                {f.label} <span className="opacity-60">· {counts[f.value]}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Featured strip (only on All view) */}
      {filter === "all" && featured.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 pb-16">
          <div className="max-w-7xl mx-auto">
            <p className="label-eyebrow mb-6">Featured pieces</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {featured.map((it, i) => <InstrumentCard key={it.id} inst={it} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* All / filtered grid */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-7xl mx-auto">
          {filter === "all" && featured.length > 0 && (
            <p className="label-eyebrow mb-6">The full collection</p>
          )}
          {loading ? (
            <p className="text-[var(--si-text-2)]">Loading...</p>
          ) : visible.length === 0 ? (
            <p className="text-[var(--si-text-2)] italic">No pieces in this category yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {visible.map((it, i) => <InstrumentCard key={it.id} inst={it} index={i} />)}
            </div>
          )}
        </div>
      </section>

      <Footer content={content || {}} />
    </div>
  );
}
